import { zustandImmerMiddleware } from '@sketchnote/shared-components';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { useLMSDashboardService } from '../api/useLMSDashboardService';

export type Member = {
  firstname: string;
  lastname: string;
  email: string;
  memberId: string;
  avatar: string;
  role: string;
};

export type Members = {
  _id: string;
  workspace: string;
  member: Member;
  access: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
};

export type Owner = {
  firstname: string;
  lastname: string;
  email: string;
  memberId: string;
  avatar: string;
  role: string;
};

export type Cards = {
  cardId: string;
  type: string;
  content: string;
  _id: string;
};

export type Sections = {
  _id: string;
  sectionId: string;
  __v: number;
  cards?: Cards[];
  createdAt: string;
  lesson: string;
  title: string;
  type: string;
  updatedAt: string;
};

export type MembersList = {
  firstname: string;
  lastname: string;
  avatar: string;
};

export interface Workspace {
  _id: string;
  organisation: string;
  owner: Owner;
  name: string;
  isCustom: boolean;
  isPublic: boolean;
  description: string;
  coverImage: string;
  coverOffset: number;
  icon?: any;
  members?: Members[];
  collapseDocuments: boolean;
  collapseBoards: boolean;
  collapseFiles: boolean;
  collpaseFavorites: boolean;
  lms?: boolean;
  workspaceId: string;
  createdAt: boolean;
  updatedAt: boolean;
  __v: number;
  access: string;
}

export interface topLession {
  _id: string;
  organisation: string;
  workspace: string;
  name: string;
  tags?: string[];
  publish: boolean;
  publishToSketchnote: boolean;
  everyoneAtOrgAccess: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
  deadline: string;
  sections?: Sections[];
  members?: MembersList[];
  noOfEnrolments: number;
  courseCompletionRate: number;
}

export type workspaceFilter = {
  sort?: string;
  search?: string;
};

export type analytics = {
  enrolmentCount?: string;
  lessonCompletionRate?: string;
  averageScore?: string;
  quizList?: any[];
  selectedQuiz?: any;
  usersList?: any[];
  questionsList?: any[];
  currentUser?: string;
  currentQuiz?: string;
  filterUsersList?: any[];
  summary?: any[];
};

type LMSDashboardStoreState = {
  workspace: Workspace[];
  topLessions: topLession[];
  lms_mode: string;
  userDetail: any;
  enrolments?: any;
  analytics?: analytics;
  lessonIsLoading: boolean;
  getWorkspace: (filter: workspaceFilter) => void;
  getTopLessions: () => void;
  setLmsMode: (lms_mode_text: string) => void;
  getLmsMode: () => void;
  fetchEnrolments: (lessonId: string) => void;
  isInstructorSidebar: boolean;
  setIsInstructorSidebar: (value: boolean) => void;
  getUserDetail: () => void;
  inviteUserOnOrg: (payload: any) => void;
  getAnalytics: (lessonId: string) => void;
  getQuizData: (
    lessonId: string,
    quizId: string,
    sort?: string,
    excAftDadlinePass?: boolean
  ) => void;
  getQuizUsersQuestions: (lessonId: string, quizId: string, memberId: string) => void;
  setCurrentQuizAndMember: (currentUser: string, currentQuiz: string) => void;
  setFilterUserListMember: (usersList: any) => void;
  getQuizSummaryData: (lessonId: string, quizId: string) => void;
  resetState: () => void;
  setLoadingData: (lessonIsLoading: boolean) => void;
};

const getInitialValue = () => [
  {
    _id: '',
    organisation: '',
    owner: {
      avatar: '',
      email: '',
      firstname: '',
      lastname: '',
      memberId: '',
      role: '',
    },
    name: '',
    isCustom: true,
    isPublic: true,
    description: '',
    coverImage: '',
    coverOffset: 0,
    icon: {},
    members: [
      {
        _id: '',
        workspace: '',
        member: {
          firstname: '',
          lastname: '',
          email: '',
          memberId: '',
          avatar: '',
          role: '',
        },
        access: '',
        createdAt: '',
        updatedAt: '',
        __v: 0,
      },
    ],
    collapseDocuments: false,
    collapseBoards: false,
    collapseFiles: false,
    collpaseFavorites: false,
    lms: true,
    workspaceId: '',
    createdAt: true,
    updatedAt: true,
    __v: 0,
    access: '',
  },
];

const getInitialValueLession = () => [
  {
    _id: '',
    organisation: '',
    workspace: '',
    name: '',
    tags: [],
    publish: true,
    publishToSketchnote: true,
    everyoneAtOrgAccess: true,
    createdAt: '',
    updatedAt: '',
    __v: 0,
    deadline: '',
    sections: [
      {
        _id: '',
        sectionId: '',
        __v: 0,
        cards: [
          {
            cardId: '',
            type: '',
            content: '',
            _id: '',
          },
        ],
        createdAt: '',
        lesson: '',
        title: '',
        type: '',
        updatedAt: '',
      },
    ],
    members: [],
    noOfEnrolments: 0,
    courseCompletionRate: 0,
  },
];

const resetTabValue = () => [
  {
    lessonIsLoading: true,
    workspace: [],
    topLessions: getInitialValueLession(),
    lms_mode: 'learner',
    userDetail: {},
    enrolments: {},
    analytics: {},
  },
];

export const useLMSDashboardStore = create<
  LMSDashboardStoreState,
  [['zustand/devtools', LMSDashboardStoreState], ['zustand/immer', LMSDashboardStoreState]]
>(
  devtools(
    zustandImmerMiddleware((set) => ({
      workspace: [],
      topLessions: getInitialValueLession(),
      lms_mode: '',
      userDetail: {},
      enrolments: {},
      analytics: {},
      lessonIsLoading: true,
      getWorkspace: async (filter: workspaceFilter) => {
        const response = await useLMSDashboardService().newfetchAllWorkspace(filter);
        set((s) => {
          s.workspace = response;
        });
      },
      getTopLessions: async () => {
        const response = await useLMSDashboardService().fetchTopLesson();
        set((s) => {
          s.topLessions = response;
        });
      },
      setLmsMode: async (lms_mode_text: string) => {
        await useLMSDashboardService().setLmsMode(lms_mode_text);
        set((s) => {
          s.lms_mode = lms_mode_text;
        });
      },
      getLmsMode: async () => {
        const response = await useLMSDashboardService().getLmsMode();
        set((s) => {
          s.lms_mode = response;
        });
      },
      getUserDetail: async () => {
        const response = await useLMSDashboardService().getUserDetail();
        set((s) => {
          s.userDetail = response;
        });
      },
      inviteUserOnOrg: async (payload: any) => {
        await useLMSDashboardService().inviteUserOnOrg(payload);
      },
      fetchEnrolments: async (lessonId: string) => {
        const response = await useLMSDashboardService().fetchEnrolments(lessonId);
        set((s) => {
          s.enrolments = response;
        });
        set((s) => {
          return { ...s, lessonIsLoading: false };
        });
      },
      getAnalytics: async (lessonId: string) => {
        const response = await useLMSDashboardService().getAnalytics(lessonId);
        if (response) {
          set((s) => {
            s.analytics = response;
          });
          set((s) => {
            return { ...s, lessonIsLoading: false };
          });
        }
      },
      getQuizData: async (
        lessonId: string,
        quizId: string,
        sort?: string,
        excAftDadlinePass?: boolean
      ) => {
        const response = await useLMSDashboardService().getQuizUsersList(
          lessonId,
          quizId,
          sort,
          excAftDadlinePass
        );

        const memberId = response?.length > 0 ? response[0]._id : '';
        let QuesResponse = [];
        if (memberId) {
          QuesResponse = await useLMSDashboardService().getQuizUsersQuestionsData(
            lessonId,
            quizId,
            memberId
          );
        }

        const summeryResponse = await useLMSDashboardService().getQuizSummaryData(lessonId, quizId);

        if (response) {
          set((s) => {
            s.analytics = {
              ...s.analytics,
              usersList: response,
              questionsList: QuesResponse,
              summary: summeryResponse,
            };
          });
        }
      },
      getQuizUsersQuestions: async (lessonId: string, quizId: string, memberId: string) => {
        const response = await useLMSDashboardService().getQuizUsersQuestionsData(
          lessonId,
          quizId,
          memberId
        );
        if (response) {
          set((s) => {
            s.analytics = { ...s.analytics, questionsList: response };
          });
        }
      },
      setCurrentQuizAndMember: async (currentUser: string, currentQuiz: string) => {
        set((s) => {
          s.analytics = { ...s.analytics, currentUser, currentQuiz };
        });
      },
      setFilterUserListMember: async (usersList: any) => {
        set((s) => {
          s.analytics = { ...s.analytics, filterUsersList: usersList };
        });
      },
      getQuizSummaryData: async (lessonId: string, quizId: string) => {
        const response = await useLMSDashboardService().getQuizSummaryData(lessonId, quizId);
        if (response) {
          set((s) => {
            s.analytics = { ...s.analytics, summary: response };
          });
        }
      },

      isInstructorSidebar: false,
      setIsInstructorSidebar: (value) => {
        set((state) => {
          state.isInstructorSidebar = value;
        });
      },
      setLoadingData: (lessonIsLoading) => {
        set((s) => {
          return { ...s, lessonIsLoading: lessonIsLoading };
        });
      },
      resetState: () => {
        set((s) => {
          return { ...s, ...resetTabValue() };
        });
      },
    }))
  )
);
